export interface TranscriptErrorStateProps {
  onRetry?: () => void;
}

/**
 * Shown when the transcription fetch fails. Stacked-papers illustration
 * (per Figma), short failure copy, and a Retry button that re-runs the
 * fetch when provided.
 */
export function TranscriptErrorState({ onRetry }: TranscriptErrorStateProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
      <StackedPapersIcon className="h-[49px] w-[57px]" />
      <div className="flex flex-col gap-2">
        <p className="text-xs font-medium leading-[15px] text-[#474C57]">
          Error generating transcript
        </p>
        <p className="text-xs leading-[1.5] text-[#474C57]">
          We couldn't process the audio. Try again, or upload a version with
          clearer sound.
        </p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="flex h-7 items-center gap-1 rounded border-[0.5px] border-[#DFE1E4] bg-white px-2 text-[13px] font-medium leading-[1.4] text-[#474C57] shadow-[0_1px_4px_-1px_rgba(0,0,0,0.12)] transition-colors hover:bg-[#F7F8F9]"
        >
          <RetryIcon className="h-4 w-4" />
          Retry
        </button>
      )}
    </div>
  );
}

function StackedPapersIcon({ className }: { className?: string }) {
  return (
    <svg
      width="57"
      height="49"
      viewBox="0 0 57 49"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect
        x="18.1748"
        y="-0.306186"
        width="40.5"
        height="40.5"
        rx="7.75"
        transform="rotate(15 18.1748 -0.306186)"
        fill="white"
      />
      <rect
        x="18.1748"
        y="-0.306186"
        width="40.5"
        height="40.5"
        rx="7.75"
        transform="rotate(15 18.1748 -0.306186)"
        stroke="#DFE1E4"
        strokeWidth="0.5"
      />
      <path
        d="M40.823 21.4425L37.8844 16.4659C37.7734 16.2813 37.6225 16.1635 37.4316 16.1123C37.2252 16.057 37.0419 16.0756 36.8816 16.1682C36.7213 16.2607 36.6142 16.4076 36.5603 16.6088C36.5091 16.7997 36.5326 16.9803 36.6308 17.1504L37.8635 19.2061L38.9692 20.838L37.2021 20.2815L27.2564 17.6166C27.0552 17.5627 26.8716 17.5826 26.7054 17.6763C26.5393 17.77 26.4285 17.9201 26.3732 18.1265C26.3193 18.3277 26.3409 18.5105 26.4379 18.6747C26.5349 18.839 26.684 18.9481 26.8852 19.002L36.8309 21.6669L38.6374 22.0763L36.866 22.9289L34.7685 24.1006C34.5998 24.1937 34.4898 24.3357 34.4387 24.5266C34.3848 24.7279 34.4041 24.9086 34.4966 25.0689C34.5892 25.2292 34.7386 25.337 34.945 25.3923C35.1359 25.4435 35.3256 25.4168 35.5139 25.3124L40.5451 22.4797C40.7387 22.3767 40.8637 22.2249 40.9202 22.0243C40.9767 21.8238 40.9443 21.6299 40.823 21.4425Z"
        fill="#90959D"
      />
      <path
        d="M24.0925 27.5363L27.0487 32.5093C27.1545 32.6925 27.3054 32.8104 27.5015 32.863C27.7078 32.9183 27.8899 32.8993 28.0476 32.806C28.2054 32.7128 28.3112 32.5655 28.3651 32.3643C28.4162 32.1734 28.3946 31.9962 28.3001 31.8326L27.0674 29.7769L25.954 28.1429L27.7232 28.6917L37.6688 31.3566C37.8701 31.4105 38.0538 31.3906 38.2199 31.2969C38.3861 31.2031 38.4961 31.0556 38.55 30.8544C38.6039 30.6532 38.5827 30.4691 38.4864 30.3023C38.3901 30.1354 38.2413 30.025 38.0401 29.9711L28.0945 27.3062L26.2858 26.9046L28.0671 26.0463L30.1625 24.8823C30.3274 24.7827 30.4348 24.64 30.4846 24.4543C30.5385 24.2531 30.5208 24.0714 30.4315 23.9091C30.3422 23.747 30.1944 23.6383 29.988 23.583C29.792 23.5304 29.6016 23.5596 29.4171 23.6705L24.3703 26.4992C24.182 26.6036 24.0599 26.7547 24.0041 26.9527C23.9483 27.1506 23.9778 27.3452 24.0925 27.5363Z"
        fill="#90959D"
      />
      <rect
        x="-0.306186"
        y="10.1763"
        width="40.5"
        height="40.5"
        rx="7.75"
        transform="rotate(-15 -0.306186 10.1763)"
        fill="white"
      />
      <rect
        x="-0.306186"
        y="10.1763"
        width="40.5"
        height="40.5"
        rx="7.75"
        transform="rotate(-15 -0.306186 10.1763)"
        stroke="#DFE1E4"
        strokeWidth="0.5"
      />
      <path
        d="M30.1822 17.687L25.1489 14.8465C24.9605 14.7421 24.7709 14.7155 24.58 14.7667C24.3736 14.822 24.2241 14.9298 24.1316 15.09C24.039 15.2503 24.0197 15.4311 24.0736 15.6323C24.1248 15.8232 24.2354 15.9678 24.4055 16.066L26.501 17.23L28.2744 18.0904L26.4658 18.492L16.5202 21.157C16.319 21.2109 16.1699 21.32 16.0729 21.4842C15.9758 21.6485 15.9549 21.8338 16.0102 22.0402C16.0642 22.2414 16.1742 22.3889 16.3404 22.4826C16.5065 22.5764 16.6902 22.5963 16.8914 22.5424L26.837 19.8775L28.6063 19.3287L27.4985 20.9528L26.2679 23.0163C26.1683 23.1813 26.1441 23.3592 26.1952 23.5501C26.2491 23.7514 26.3562 23.8983 26.5165 23.9908C26.6768 24.0833 26.8602 24.102 27.0666 24.0467C27.2575 23.9955 27.4084 23.8776 27.5193 23.693L30.4601 18.7242C30.5761 18.5382 30.6085 18.3443 30.5572 18.1424C30.5058 17.9404 30.3808 17.7887 30.1822 17.687Z"
        fill="#90959D"
      />
      <path
        d="M18.7399 31.3297L23.7867 34.1583C23.9698 34.2641 24.1595 34.2908 24.3556 34.2382C24.5619 34.1829 24.7101 34.0755 24.8001 33.9158C24.8901 33.7562 24.9081 33.5758 24.8542 33.3746C24.803 33.1837 24.6956 33.041 24.532 32.9466L22.4366 31.7826L20.6554 30.9243L22.462 30.5149L32.4076 27.85C32.6088 27.796 32.7579 27.687 32.855 27.5227C32.952 27.3585 32.9735 27.1757 32.9196 26.9745C32.8657 26.7733 32.7553 26.6245 32.5885 26.5282C32.4216 26.4318 32.2376 26.4106 32.0364 26.4645L22.0907 29.1294L20.3236 29.6859L21.437 28.052L22.6697 25.9963C22.7628 25.8275 22.7844 25.6503 22.7347 25.4645C22.6808 25.2633 22.5746 25.1148 22.4162 25.0189C22.2578 24.9232 22.0754 24.9029 21.869 24.9582C21.6729 25.0107 21.5227 25.1312 21.4183 25.3195L18.462 30.2926C18.3511 30.4772 18.321 30.6691 18.3716 30.8685C18.4223 31.0678 18.5451 31.2215 18.7399 31.3297Z"
        fill="#90959D"
      />
      <g filter="url(#stackedPapersShadow)">
        <rect
          x="8.49219"
          y="4.49512"
          width="40"
          height="40"
          rx="7.5"
          fill="white"
        />
        <rect
          x="8.24219"
          y="4.24512"
          width="40.5"
          height="40.5"
          rx="7.75"
          stroke="#DFE1E4"
          strokeWidth="0.5"
        />
        <path
          d="M35.9922 26.7451V24.1201C35.9922 23.225 35.6366 22.3666 35.0037 21.7336C34.3707 21.1007 33.5123 20.7451 32.6172 20.7451H31.1172C30.8188 20.7451 30.5327 20.6266 30.3217 20.4156C30.1107 20.2046 29.9922 19.9185 29.9922 19.6201V18.1201C29.9922 17.225 29.6366 16.3666 29.0037 15.7336C28.3707 15.1007 27.5123 14.7451 26.6172 14.7451H24.7422M24.7422 27.4951H32.2422M24.7422 30.4951H28.4922M26.9922 14.7451H22.1172C21.4962 14.7451 20.9922 15.2491 20.9922 15.8701V33.1201C20.9922 33.7411 21.4962 34.2451 22.1172 34.2451H34.8672C35.4882 34.2451 35.9922 33.7411 35.9922 33.1201V23.7451C35.9922 21.3582 35.044 19.069 33.3561 17.3812C31.6683 15.6933 29.3791 14.7451 26.9922 14.7451Z"
          stroke="#90959D"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <filter
          id="stackedPapersShadow"
          x="4.99219"
          y="1.99512"
          width="47"
          height="47"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology
            radius="1"
            operator="erode"
            in="SourceAlpha"
            result="effect1_dropShadow"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="2" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

function RetryIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M3.66927 6.567L3.22951 5.90969C3.19171 5.85369 3.14318 5.80575 3.08672 5.76863C3.03027 5.73151 2.96702 5.70596 2.90062 5.69345C2.83423 5.68095 2.76601 5.68173 2.69992 5.69577C2.63383 5.7098 2.57119 5.73681 2.5156 5.77522C2.27885 5.93617 2.21447 6.25808 2.37178 6.49327L3.51195 8.1973C3.54975 8.2533 3.59828 8.30124 3.65474 8.33836C3.71119 8.37548 3.77444 8.40103 3.84084 8.41353C3.90723 8.42604 3.97545 8.42525 4.04154 8.41122C4.10763 8.39718 4.17028 8.37018 4.22586 8.33177L5.94182 7.16564C6.05567 7.08926 6.13469 6.97095 6.16164 6.83652C6.18858 6.70209 6.16126 6.56246 6.08564 6.4481C6.04788 6.39206 5.99938 6.34407 5.94295 6.3069C5.88651 6.26974 5.82327 6.24413 5.75687 6.23158C5.69047 6.21902 5.62224 6.21977 5.55613 6.23376C5.49003 6.24776 5.42735 6.27473 5.37174 6.31311L4.67393 6.78818C5.18482 5.0852 6.74659 3.84638 8.59339 3.84638C10.8556 3.84638 12.6899 5.70616 12.6899 8C12.6899 10.2938 10.8556 12.1536 8.59339 12.1536C8.31042 12.1536 8.08094 12.3862 8.08094 12.6728C8.08094 12.9594 8.31042 13.192 8.59339 13.192C11.4215 13.192 13.7143 10.8676 13.7143 8C13.7143 5.13244 11.4215 2.80797 8.59339 2.80797C6.2549 2.80797 4.28297 4.39673 3.66927 6.567Z"
        fill="#474C57"
      />
    </svg>
  );
}
